import { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Checkbox,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { skipToken } from "@reduxjs/toolkit/query";

import style from "./user.module.css";
import {
  useGetRoleAPIServicesQuery,
  useGetUserRoleDetailsQuery,
  useUpdateUserRoleStatusMutation,
} from "../../services/api";
import { Button, LabelBox } from "../../components";
import { ConfirmationDialog } from "../../components/Dialog";
import { colors } from "../../theme/colors";
import { isRoleApiServicesResponse, isUserRole } from "./userShared";

/**
 * Renders the user role details page with associated API services.
 *
 * Returns:
 *   JSX.Element: User role detail view with services and status section.
 *
 * Notes:
 *   TODO: Confirm the expected behavior when the route param is missing.
 */
const UserRoleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const roleId = Number(id);
  const hasValidId = Number.isFinite(roleId) && roleId > 0;
  const [activateModalVisible, setActivateModalVisible] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [updateUserRoleStatus] = useUpdateUserRoleStatusMutation();

  // Skip queries when route params are invalid to avoid unnecessary requests.
  const userRoleDetailsQuery = useGetUserRoleDetailsQuery(
    hasValidId ? id ?? "" : skipToken
  );
  const userRoleDetails = isUserRole(userRoleDetailsQuery.data)
    ? userRoleDetailsQuery.data
    : undefined;

  const roleApiServicesQuery = useGetRoleAPIServicesQuery(
    hasValidId ? { userRoleId: roleId } : skipToken
  );
  const apiServices = isRoleApiServicesResponse<Service>(
    roleApiServicesQuery.data
  )
    ? roleApiServicesQuery.data
    : undefined;
  const { isLoading, isFetching } = roleApiServicesQuery;

  const handleActivate = async () => {
    if (!userRoleDetails?.id) {
      return;
    }
    try {
      setIsActivating(true);
      await updateUserRoleStatus({
        ids: [userRoleDetails.id],
        is_active: true,
        deactivate_reason: "",
      }).unwrap();
      setActivateModalVisible(false);
    } catch (error: any) {
      setActivateModalVisible(false);
      setMessage({
        type: "error",
        text: error?.message || error?.data?.detail || "Something went wrong!",
      });
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: "30px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          border: `1px solid ${colors.grey}`,
          borderRadius: "4px",
          height: "100%",
        }}
      >
        {isLoading || isFetching ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "calc(100vh - 97px)",
            }}
          >
            {/* Keep layout height stable while async data loads to avoid content jump. */}
            <CircularProgress size={40} sx={{ marginLeft: "10px" }} />
          </Box>
        ) : (
          <Box sx={{ height: "100%", padding: "15px 40px" }}>
            <Typography className={style.userDetailHeading}>
              User Role Details
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Name</Typography>
                <Typography className={style.value}>
                  {userRoleDetails?.name}
                </Typography>
              </Box>
              <Box
                className={style.detailsRow}
                sx={{ alignItems: "flex-start !important" }}
              >
                <Typography className={style.title}>Services</Typography>
                <Box className={style.value}>
                  <Grid container columnSpacing={2}>
                    {/* Render as a read-only checklist to emphasize assigned services. */}
                    {apiServices?.api_services?.map((service: Service) => {
                      return (
                        <Grid
                          item
                          xs={(apiServices?.api_services?.length ?? 0) > 10 ? 6 : 12}
                          key={service.id}
                        >
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            paddingY={1}
                          >
                            <Checkbox
                              checked={true}
                              disableTouchRipple
                              sx={{
                                transform: "scale(0.92)",
                                padding: "0px",
                              }}
                              disabled
                            />
                            <Typography
                              sx={{
                                marginLeft: 1,
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {service.endpoint_name}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Box>
            </Box>
            {userRoleDetails?.deactivate_reason && (
              <Box>
                {/* Deactivation details are shown only when a prior deactivation exists. */}
                <Typography className={style.userDetailHeading}>
                  Deactivation Details
                </Typography>
                <Box>
                  <Box className={style.detailsRow}>
                    <Typography className={style.title}>
                      Reason for deactivation
                    </Typography>
                    <Typography className={style.value}>
                      {userRoleDetails?.deactivate_reason}
                    </Typography>
                  </Box>
                  <Box className={style.detailsRow}>
                    <Typography className={style.title}>
                      Date of Deactivation
                    </Typography>
                    <Typography className={style.value}>
                      {userRoleDetails?.deactivated_at
                        ? dayjs(userRoleDetails?.deactivated_at).format(
                            "MMMM DD YYYY, hh:mm:ss A"
                          )
                        : "-"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            {message && (
              <Box mt={2}>
                <LabelBox
                  boxProps={{
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                  }}
                  data={message}
                  onClose={() => setMessage(null)}
                />
              </Box>
            )}
            <Box className={style.buttons} sx={{ marginBottom: "20px" }}>
              <Button
                sx={{
                  width: "200px",
                  height: "55px",
                  ":hover": { backgroundColor: "transparent" },
                }}
                variant="outlined"
                onClick={() => navigate(-1)}
                label={"Close"}
              />
              {userRoleDetails?.is_active ? (
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: colors.primary },
                  }}
                  variant="contained"
                  label={"Edit"}
                  onClick={() =>
                    navigate(`/user/roles/edit/${userRoleDetails?.id}`)
                  }
                />
              ) : (
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: colors.primary },
                  }}
                  variant="contained"
                  label={"Activate"}
                  onClick={() => setActivateModalVisible(true)}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
      {activateModalVisible && (
        <ConfirmationDialog
          visible={activateModalVisible}
          message={`Are you sure you want to activate "${userRoleDetails?.name}"?`}
          onClose={() => setActivateModalVisible(false)}
          onConfirm={handleActivate}
          loading={isActivating}
        />
      )}
    </Box>
  );
};

export default UserRoleDetails;
